<template>
  <v-container fluid class="pt-0">
    <v-row>
      <v-col cols="12" class="px-0 pt-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          dense
          class="elevation-1 pt-2 body-1"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          show-expand
          item-key="id"
          @page-count="pageCount = $event"
          :key="documentsTableKey"
          sort-by="updatedAt" :sort-desc="true"
        >
          <!--Name-->
          <template v-slot:item.name="{ item }" >
            <v-btn text small class="px-0 flex-shrink-0" @click="onDownloadClick(item)">
              <v-icon class="mr-1" x-small :color="getColorByFileName(item.name)">file_present</v-icon>
              <span class="text-ellipsis text-lowercase body-1" :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'">{{ item.name }}</span>
            </v-btn>
          </template>
          <!--Type-->
          <template v-slot:item.typeId="{ item }" >
            <span class="body-2 primary--text"><v-icon class="me-2" small color="lightSecondary">{{ getDocumentIconById(item.typeId) }}</v-icon>{{ getDocumentType(item) }}</span>
          </template>
          <!--Game-->
          <template v-slot:item.gameId="{ item }" >
            <router-link :to="'/game/'+item.gameId" class="nav-link body-2" :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'"><span v-if="item.gameId">{{ item.gameName }}</span></router-link>
          </template>
          <!--Player-->
          <template v-slot:item.playerId="{ item }" >
            <router-link :to="'/player/'+item.playerId" class="nav-link body-2" :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'"><span v-if="item.playerId">{{ item.playerName }}</span></router-link>
          </template>
          <!--TS Updated-->
          <template v-slot:item.updatedAt="{ item }" >
            <span v-if="item.updatedAt">{{ formatTimestamp(item.updatedAt) }}</span>
            <span v-else-if="item.createdAt">{{ formatTimestamp(item.createdAt) }}</span>
            <span v-else>-</span>
          </template>
          <!--Expand-->
          <template #item.data-table-expand="{ item, expand, isExpanded }">
            <v-btn v-if="item.notes" icon class="v-data-table__expand-icon" :class="{'v-data-table__expand-icon--active' : isExpanded}"
              @click="expand(!isExpanded)"><v-icon>expand_more</v-icon></v-btn>
          </template>
          <!--Actions-->
          <template v-slot:item.actions="{ item }" >
            <v-btn elevation="0" icon x-small class="ms-1" color="grey lighten-1" @click="onDownloadClick(item)">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <AddDocument
              v-if="item.createdBy === currentUser.username"
              :type="type"
              buttonType="editButton"
              :isEditing="true"
              :documentToUpdate="item"
              :documentTypes="documentTypes"
              @dialogClosed="handleDialogClosed"
              @postSearch="handlePostSearch($event)"
            ></AddDocument>
            <v-btn :type="type" elevation="0" icon x-small class="ms-1" color="grey lighten-1"
              v-if="item.createdBy === currentUser.username"
              @click="onDeleteClick(item)"
            >
              <v-icon small>delete</v-icon>
            </v-btn>
          </template>
          <!--Expanded Notes-->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="py-2 px-4 body-1 primary--text" elevation="0" :text-color="$vuetify.theme.dark ? 'black' : 'grey lighten-5'">
              <!--Final notes-->
              <span class="font-weight-bold monospace-font body-2" v-if="item.notes">{{ $t('general.notes') }}:</span>
              <p v-if="item.notes" class="">{{ item.notes }}</p>
            </td>
          </template>
        </v-data-table>
        <div v-if="pageCount > 1" class="text-center pt-2">
          <v-pagination v-model="page" :length="pageCount" class="monospace-font" color="secondary" circle></v-pagination>
        </div>
      </v-col>
    </v-row>
    <!--Delete analysis confirmation-->
    <ConfirmationModal
      @confirmation="onRemoveDocumentConfirmation($event)"
      ref="confirmationRemoveModal"
      title="documents.deleteDocument"
      message="documents.deleteDocumentMessage"
    ></ConfirmationModal>
  </v-container>
</template>

<script>

import * as _ from "lodash";
import * as moment from 'moment';
import ConfirmationModal from "../../base/ConfirmationModal.vue";
import DocumentService from "../../../services/document.service";
import { getDocumentIconById, getColorByFileName } from "../../../utils/functions";
import AddDocument from "./AddDocument.vue";

export default {
  name: "DocumentsTable",
  props: {
    items: {
      type: Array,
      default: () => []
    },
    documentTypes: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'player'
    }
  },
  components: { ConfirmationModal, AddDocument },
  data() {
    return {
      documentToDelete: null,
      failed_image: false,
      itemsPerPage: 7,
      page: 1,
      pageCount: 0,
      documentsTableKey: 0,
      expanded: [],
      singleExpand: false,
      headers: [
        {
          text: this.$t('collections.name'),
          align: "start",
          value: "name",
          sortable: false,
        },
        {
          text: this.$t('general.type'),
          align: "start",
          value: "typeId",
          sortable: false,
          width: 200
        },
        {
          text: this.$t('blog.author'),
          align: "start",
          value: "createdBy",
          sortable: false,
        },
        {
          text: this.$t('scoutingAnalysis.lastEdited'),
          align: "start",
          value: "updatedAt",
          sortable: false,
        },
        {
          text: this.$t('documents.seeNotes'),
          value: 'data-table-expand',
          align: "center",
        },
        {
          text: "",
          align: "center",
          value: "actions",
          sortable: false,
          width: 110
        },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  created() {
    if (this.type === 'team') this.headers.splice(2, 0, { text: this.$t('documents.match'), slign: "start", value: "gameId", sortable: false });
    else if (this.type === 'game') this.headers.splice(2, 0, { text: this.$t('general.player'), slign: "start", value: "playerId", sortable: false });
  },
  methods: {
    formatTimestamp(date) {
      const formattedDate = moment(date, "DD/MM/YYYY HH:mm").format("DD/MM/YYYY");
      return formattedDate;
    },
    onDeleteClick(document) {
      this.documentToDelete = document;
      this.$refs.confirmationRemoveModal.open();
    },
    async onRemoveDocumentConfirmation(confirmation){
      if (confirmation) {
        if (this.type === 'player') await DocumentService.deleteDocument(this.documentToDelete);
        else if (this.type === 'team') await DocumentService.deleteTeamDocument(this.documentToDelete);
        else if (this.type === 'game') {
          if (this.documentToDelete.typeId === 2) await DocumentService.deleteDocument(this.documentToDelete); //player_report
          else await DocumentService.deleteGameDocument(this.documentToDelete);
        };
        const postSearchEvent = { operation: "delete", filename: this.documentToDelete.name, documentType: this.documentTypes.filter(d => d.id === this.documentToDelete.typeId)[0] };
        this.handlePostSearch(postSearchEvent);
        this.handleDialogClosed();
      };
      this.documentToDelete = null;
      this.$refs.confirmationRemoveModal.close();
      this.handleDialogClosed();
    },
    handleDialogClosed() {
      this.documentsTableKey++;
      this.$emit('documentChanged');
    },
    handlePostSearch(event) {
      this.$emit("postSearch", event);
    },
    getDocumentIconById(id) {
      return getDocumentIconById(id);
    },
    getColorByFileName(name) {
      return getColorByFileName(name);
    },
    getDocumentType(document) {
      const typeId = this.type === 'game' && document.typeId === 2 ? 8 : document.typeId;
      const type = this.documentTypes.filter(d => d.id === typeId)[0];
      return this.$t(`documents.types.${type.code}`);
    },
    async onDownloadClick(document) {
      const filename = document.name;
      let urlResponse;
      if (this.type === 'player') urlResponse = await DocumentService.getDownloadURL(document.id, filename);
      else if (this.type === 'team') urlResponse = await DocumentService.getTeamDownloadURL(document.id, filename);
      else if (this.type === 'game') {
        if (document.typeId === 2) urlResponse = await DocumentService.getDownloadURL(document.id, filename); //player_report
        else urlResponse = await DocumentService.getGameDownloadURL(document.id, filename);
      };
      const url = urlResponse.data;
      window.open(url);
    },
  }
};
</script>

<style scoped>
  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    padding-top: 0.8em;
    padding-bottom: 0.8em;
  }
</style>



// WEBPACK FOOTER //
// src/views/scouting-analysis/documents/DocumentsTable.vue